import {
  API_TYPES,
  type GetWorkerRequirementsResponse,
} from "@clipboard-health/contract-worker-app-bff";
import { isDefined } from "@clipboard-health/util-ts";
import { type UseGetQueryOptions } from "@src/appV2/api";
import { useWorkerCustomToBaseQualificationNameMap } from "@src/appV2/Qualifications/useWorkerCustomToBaseQualificationNameMap";
import { type ModalShiftData } from "@src/appV2/redesign/Shift/types";
import { type AxiosError } from "axios";
import { isEmpty } from "lodash";

import { mergeWorkerRequirementWithIncludedResources } from "../mergeWorkerRequirementWithIncludedResources";
import { type WorkerRequirement } from "../types";
import { requirementRelationship, useGetWorkerRequirements } from "../useGetWorkerRequirements";

interface UseGetBlockingRequirementsForShiftProps {
  shift?: ModalShiftData;
  qualification?: string;
}

export function useGetBlockingRequirementsForShift(
  props: UseGetBlockingRequirementsForShiftProps,
  options: UseGetQueryOptions<GetWorkerRequirementsResponse, AxiosError, WorkerRequirement[]> = {}
) {
  const { shift, qualification } = props;
  const { workplace, attributes: { end, qualification: shiftQualification } = {} } = shift ?? {};

  const workerCustomToBaseQualificationNameMap = useWorkerCustomToBaseQualificationNameMap();
  const baseQualificationName = workerCustomToBaseQualificationNameMap.get(
    qualification ?? shiftQualification ?? ""
  );

  const qualifications = [
    ...new Set([qualification ?? shiftQualification, baseQualificationName]),
  ].filter(isDefined);

  return useGetWorkerRequirements(
    {
      filter: {
        workplaceIds: [workplace?.id ?? ""],
        qualifications,
        expiresAt: { gte: end ?? "" },
        requiredForBooking: true,
        isValid: false,
      },
      include: [...requirementRelationship, API_TYPES.requirementMappings],
    },
    {
      select: mergeWorkerRequirementWithIncludedResources,
      ...options,
      enabled: isDefined(shift) && !isEmpty(qualifications) && (options.enabled ?? true),
    }
  );
}
